import type { ILeasingRequest } from '@/shared/types/leasing-request.types';
import type {
  TRequestApi,
  TRequestDecisionApi,
  TRequestDetailApi
} from '@/shared/types/requests-api.types';

import {
  resolveApiCardStatus,
  resolveApiRequestStage,
  resolveVehicleType
} from '../constants/requests';
import type { IRequest } from '../types/request.types';

const resolveAmount = (value: string | number | null | undefined): number => {
  if (value === null || value === undefined || value === '') {
    return 0;
  }

  return Number(value);
};

const resolveDownPaymentPct = (
  downPaymentAmount: number,
  vehicleCost: number
): number => {
  if (!vehicleCost) {
    return 0;
  }

  return Math.round((downPaymentAmount / vehicleCost) * 100);
};

const resolveLatestDecision = (
  decisions: TRequestDecisionApi[]
): TRequestDecisionApi | null => {
  if (!decisions.length) {
    return null;
  }

  return decisions[decisions.length - 1] ?? null;
};

const resolveVehicleDetails = (
  leasingSubject: TRequestDetailApi['leasingSubject']
): string => {
  const details = [
    leasingSubject?.year ? String(leasingSubject.year) : null,
    leasingSubject?.equipmentName,
    leasingSubject?.engineType?.name
  ].filter(Boolean);

  return details.join(' · ') || '-';
};

const resolveDownPaymentMethod = (
  method: string | null | undefined
): 'IOKA' | 'CASH' | null => {
  if (method === 'IOKA' || method === 'CASH') {
    return method;
  }

  return null;
};

const resolveRequestFile = (
  files: TRequestDetailApi['files'],
  fileTypeCode: string
): { name: string; link: string } | null => {
  const requestFile = files.find(
    (file) =>
      file.fileType.code === fileTypeCode && !file.isDeleted && file.link
  );

  if (!requestFile) {
    return null;
  }

  return {
    name: requestFile.originalName,
    link: requestFile.link
  };
};

export const mapApiRequestToCard = (
  request: TRequestApi,
  useDecision = false
): IRequest => {
  const { leasingSubject } = request;
  const decision = useDecision ? request.decisions[0] : undefined;

  return {
    id: request.uuid,
    number: request.code,
    kind: 'request',
    status: resolveApiCardStatus(request.status.code),
    statusCode: request.status.code,
    statusLabel: request.status.name,
    createDate: decision ? decision.createDate : request.createDate,
    vehicleName: `${leasingSubject?.brand?.name ?? '-'} ${leasingSubject?.model?.name ?? '-'}`,
    vehicleDetails: leasingSubject?.year ? String(leasingSubject.year) : '',
    imageUrl: leasingSubject?.model?.imgSrc ?? '',
    vehiclePrice: leasingSubject?.cost ?? 0,
    downPayment: Number(
      decision?.downPaymentAmount ?? request.downPaymentAmount
    ),
    termMonths: decision ? decision.loanPeriod : request.loanPeriod,
    vehicleTypeLabel: leasingSubject?.condition ?? ''
  };
};

interface IStageResolutionParams {
  isCompleted: boolean;
  isTelemetryInstalled: boolean;
  isVinCodeAssigned: boolean;
  isDownPaymentPaid: boolean;
  isAwaitingDownPayment: boolean;
  baseStage: ReturnType<typeof resolveApiRequestStage>;
}

const resolveStageWithContracts = ({
  isCompleted,
  isTelemetryInstalled,
  isVinCodeAssigned,
  isDownPaymentPaid,
  isAwaitingDownPayment,
  baseStage
}: IStageResolutionParams) => {
  if (isCompleted) return 'contract' as const;
  if (isTelemetryInstalled) return 'issue' as const;
  if (isVinCodeAssigned) return 'preparing' as const;
  if (isDownPaymentPaid) return 'registration' as const;
  if (isAwaitingDownPayment) return 'payment' as const;
  return baseStage;
};

export const mapApiRequestToLeasingRequest = (
  request: TRequestDetailApi
): ILeasingRequest => {
  const { leasingSubject } = request;
  const latestDecision = resolveLatestDecision(request.decisions);
  const vehicleCost = leasingSubject?.cost ?? 0;
  const downPayment = Number(
    latestDecision?.downPaymentAmount ?? request.downPaymentAmount
  );
  const requestAmount = Number(
    latestDecision?.requestAmount ?? request.loanAmount
  );
  const term = latestDecision?.loanPeriod ?? request.loanPeriod;
  const price = vehicleCost;

  const latestContract = request.contracts?.length
    ? request.contracts[request.contracts.length - 1]
    : null;
  const contractId = latestContract?.uuid;
  const downPaymentMethod = resolveDownPaymentMethod(
    latestContract?.downPaymentMethod
  );
  const downPaymentStatus = latestContract?.downPaymentStatus;
  const downPaymentReceipt = resolveRequestFile(
    request.files,
    'CLIENT_DOWN_PAYMENT'
  );
  const paymentOrder = resolveRequestFile(
    request.files,
    'DOWN_PAYMENT_INVOICE'
  );
  const isDownPaymentPaid = downPaymentStatus === 'PAID';
  const isAwaitingDownPayment =
    downPaymentStatus === 'AWAITING_PAYMENT' ||
    downPaymentStatus === 'PENDING' ||
    downPaymentStatus === 'PENDING_CONFIRMATION';

  const isSaleDocumentSigned = isAwaitingDownPayment || isDownPaymentPaid;
  const isLeaseDocumentSigned = request.files.some(
    (file) =>
      file.fileType.code === 'LEASING_CLIENT_SALES_CONTRACT_SIGNED_FULLY'
  );
  const areBothDocumentsSigned = isSaleDocumentSigned && isLeaseDocumentSigned;

  const resolvedStage = areBothDocumentsSigned
    ? 'payment'
    : resolveApiRequestStage(request.status.code);
  const isVinCodeAssigned = Boolean(request.companyProperty?.vinCode);
  const isTelemetryInstalled = Boolean(
    request.companyProperty?.isTelemetryInstalled
  );
  const isCompleted = request.status.code === 'completed';

  const resolvedStageWithPayment = resolveStageWithContracts({
    isCompleted,
    isTelemetryInstalled,
    isVinCodeAssigned,
    isDownPaymentPaid,
    isAwaitingDownPayment,
    baseStage: resolvedStage
  });

  return {
    id: request.uuid,
    number: request.code,
    stage: resolvedStageWithPayment,
    statusLabel: request.status.name,
    statusCode: request.status.code,
    createDate: request.createDate,
    decisionUuid: latestDecision?.uuid,
    isOfferApproved: request.status.code === 'approved',
    isOfferAcceptedByClient:
      latestDecision?.status.code === 'approved_by_client',
    isSaleDocumentSigned,
    isLeaseDocumentSigned,
    salesDocumentSigningLink: request.salesDocumentSigningLink,
    contractId,
    downPaymentMethod,
    downPaymentReceiptName: downPaymentReceipt?.name ?? null,
    downPaymentReceiptLink: downPaymentReceipt?.link ?? null,
    paymentOrderName: paymentOrder?.name ?? null,
    paymentOrderLink: paymentOrder?.link ?? null,
    isAwaitingDownPayment,
    isDownPaymentPaid,
    isVinCodeAssigned,
    vinCode: request.companyProperty?.vinCode ?? undefined,
    isTelemetryInstalled,
    isCompleted,
    vehicleName: `${leasingSubject?.brand?.name ?? '-'} ${leasingSubject?.model?.name ?? '-'}`,
    vehicleDetails: resolveVehicleDetails(leasingSubject),
    imageUrl: leasingSubject?.model?.imgSrc ?? '',
    vehicleType: resolveVehicleType(leasingSubject?.condition),
    vehicleTypeLabel: leasingSubject?.condition ?? undefined,
    price,
    requestAmount,
    downPayment,
    downPaymentPct: resolveDownPaymentPct(downPayment, vehicleCost),
    balloon: resolveAmount(latestDecision?.balloonAmount),
    monthlyPayment: resolveAmount(latestDecision?.firstMonthPayment),
    term,
    rate: resolveAmount(latestDecision?.rate)
  };
};
